import { supabase, isSchemaError } from '../lib/supabase';
import errorTrackingService from './errorTrackingService';

// Helper to get current user session info
const getCurrentUserInfo = async () => {
  try {
    const { data: { session } } = await supabase?.auth?.getSession();
    return {
      userId: session?.user?.id || null,
      userEmail: session?.user?.email || null,
      sessionExpiry: session?.expires_at ? new Date(session.expires_at * 1000)?.toISOString() : null
    };
  } catch (err) {
    return { userId: null, userEmail: null, sessionExpiry: null };
  }
};

// Enhanced error handler that captures all context
const handleSupabaseError = async (error, context = {}) => {
  if (!error) return;

  const userInfo = await getCurrentUserInfo();
  
  errorTrackingService?.captureError(error, {
    ...context,
    ...userInfo,
    screen: context?.screen || window.location?.pathname,
    timestamp: new Date()?.toISOString()
  });

  if (isSchemaError(error)) {
    console.error('Schema error:', error?.message);
  }
};

// Helper functions for case conversion
const toSnakeCase = (str) => str?.replace(/[A-Z]/g, letter => `_${letter?.toLowerCase()}`);

const toCamelCase = (str) => str?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());

const convertKeysToCamelCase = (obj) => {
  if (Array.isArray(obj)) {
    return obj?.map(item => convertKeysToCamelCase(item));
  }
  if (obj !== null && typeof obj === 'object') {
    return Object.keys(obj)?.reduce((acc, key) => {
      acc[toCamelCase(key)] = convertKeysToCamelCase(obj?.[key]);
      return acc;
    }, {});
  }
  return obj;
};

const convertKeysToSnakeCase = (obj) => {
  if (Array.isArray(obj)) {
    return obj?.map(item => convertKeysToSnakeCase(item));
  }
  if (obj !== null && typeof obj === 'object') {
    return Object.keys(obj)?.reduce((acc, key) => {
      acc[toSnakeCase(key)] = convertKeysToSnakeCase(obj?.[key]);
      return acc;
    }, {});
  }
  return obj;
};

// User Profile Operations
export const getUserProfile = async (userId) => {
  try {
    const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      }
      if (error?.code === 'PGRST116') return null;
      console.log('Get user profile error:', error?.message);
      return null;
    }

    return convertKeysToCamelCase(data);
  } catch (error) {
    console.error('Get user profile error:', error);
    throw error;
  }
};

export const updateUserProfile = async (userId, updates) => {
  try {
    const snakeCaseUpdates = convertKeysToSnakeCase(updates);
    const { data, error } = await supabase?.from('user_profiles')?.update(snakeCaseUpdates)?.eq('id', userId)?.select()?.single();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      }
      console.log('Update user profile error:', error?.message);
      return null;
    }

    return convertKeysToCamelCase(data);
  } catch (error) {
    console.error('Update user profile error:', error);
    throw error;
  }
};

// Notification Preferences Operations
export const getNotificationPreferences = async (userId) => {
  try {
    const { data, error } = await supabase?.from('notification_preferences')?.select('*')?.eq('user_id', userId)?.single();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      }
      if (error?.code === 'PGRST116') return null;
      console.log('Get notification preferences error:', error?.message);
      return null;
    }

    return convertKeysToCamelCase(data);
  } catch (error) {
    console.error('Get notification preferences error:', error);
    throw error;
  }
};

export const updateNotificationPreferences = async (userId, updates) => {
  try {
    const snakeCaseUpdates = convertKeysToSnakeCase(updates);
    const { data, error } = await supabase?.from('notification_preferences')?.update(snakeCaseUpdates)?.eq('user_id', userId)?.select()?.single();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      }
      console.log('Update notification preferences error:', error?.message);
      return null;
    }

    return convertKeysToCamelCase(data);
  } catch (error) {
    console.error('Update notification preferences error:', error);
    throw error;
  }
};

// Budget Categories Operations
export const getBudgetCategories = async (userId) => {
  try {
    const { data, error } = await supabase?.from('budget_categories')?.select('*')?.eq('user_id', userId)?.order('created_at', { ascending: false });

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      }
      if (error?.code === 'PGRST116') return [];
      console.log('Get budget categories error:', error?.message);
      return [];
    }

    return convertKeysToCamelCase(data || []);
  } catch (error) {
    console.error('Get budget categories error:', error);
    throw error;
  }
};

export const createBudgetCategory = async (userId, category) => {
  try {
    const snakeCaseCategory = convertKeysToSnakeCase({ ...category, userId });
    const { data, error } = await supabase?.from('budget_categories')?.insert(snakeCaseCategory)?.select()?.single();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      }
      console.log('Create budget category error:', error?.message);
      return null;
    }

    return convertKeysToCamelCase(data);
  } catch (error) {
    console.error('Create budget category error:', error);
    throw error;
  }
};

export const updateBudgetCategory = async (categoryId, updates) => {
  try {
    const snakeCaseUpdates = convertKeysToSnakeCase(updates);
    const { data, error } = await supabase
      ?.from('budget_categories')
      ?.update(snakeCaseUpdates)
      ?.eq('id', categoryId)
      ?.select()
      ?.single();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      }
      console.log('Update budget category error:', error?.message);
      return null;
    }

    return convertKeysToCamelCase(data);
  } catch (error) {
    console.error('Update budget category error:', error);
    throw error;
  }
};

// Delete Budget Category
export const deleteBudgetCategory = async (categoryId) => {
  try {
    const { error } = await supabase
      ?.from('budget_categories')
      ?.delete()
      ?.eq('id', categoryId);

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      }
      console.log('Delete budget category error:', error?.message);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Delete budget category error:', error);
    throw error;
  }
};

// Financial Goals Operations
export const getFinancialGoals = async (userId) => {
  try {
    const { data, error } = await supabase?.from('financial_goals')?.select('*')?.eq('user_id', userId)?.order('created_at', { ascending: false });

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      }
      if (error?.code === 'PGRST116') return [];
      console.log('Get financial goals error:', error?.message);
      return [];
    }

    return convertKeysToCamelCase(data || []);
  } catch (error) {
    console.error('Get financial goals error:', error);
    throw error;
  }
};

export const createFinancialGoal = async (userId, goal) => {
  try {
    const snakeCaseGoal = convertKeysToSnakeCase({ ...goal, userId });
    const { data, error } = await supabase?.from('financial_goals')?.insert(snakeCaseGoal)?.select()?.single();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      }
      console.log('Create financial goal error:', error?.message);
      return null;
    }

    return convertKeysToCamelCase(data);
  } catch (error) {
    console.error('Create financial goal error:', error);
    throw error;
  }
};

export const updateFinancialGoal = async (goalId, updates) => {
  try {
    const snakeCaseUpdates = convertKeysToSnakeCase(updates);
    const { data, error } = await supabase
      ?.from('financial_goals')
      ?.update(snakeCaseUpdates)
      ?.eq('id', goalId)
      ?.select()
      ?.single();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      }
      console.log('Update financial goal error:', error?.message);
      return null;
    }

    return convertKeysToCamelCase(data);
  } catch (error) {
    console.error('Update financial goal error:', error);
    throw error;
  }
};

// Delete Financial Goal
export const deleteFinancialGoal = async (goalId) => {
  try {
    const { error } = await supabase
      ?.from('financial_goals')
      ?.delete()
      ?.eq('id', goalId);

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      }
      console.log('Delete financial goal error:', error?.message);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Delete financial goal error:', error);
    throw error;
  }
};

// Financial Systems Operations (Atomic Habits)
export const getFinancialSystems = async (userId) => {
  try {
    const { data, error } = await supabase?.from('financial_systems')?.select('*')?.eq('user_id', userId)?.order('created_at', { ascending: false });

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      }
      if (error?.code === 'PGRST116') return [];
      console.log('Get financial systems error:', error?.message);
      return [];
    }

    return convertKeysToCamelCase(data || []);
  } catch (error) {
    console.error('Get financial systems error:', error);
    throw error;
  }
};

export const createFinancialSystem = async (userId, system) => {
  try {
    const snakeCaseSystem = convertKeysToSnakeCase({ ...system, userId });
    const { data, error } = await supabase?.from('financial_systems')?.insert(snakeCaseSystem)?.select()?.single();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      }
      console.log('Create financial system error:', error?.message);
      return null;
    }

    return convertKeysToCamelCase(data);
  } catch (error) {
    console.error('Create financial system error:', error);
    throw error;
  }
};

export const updateFinancialSystem = async (systemId, updates) => {
  try {
    const snakeCaseUpdates = convertKeysToSnakeCase(updates);
    const { data, error } = await supabase?.from('financial_systems')?.update(snakeCaseUpdates)?.eq('id', systemId)?.select()?.single();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      }
      console.log('Update financial system error:', error?.message);
      return null;
    }

    return convertKeysToCamelCase(data);
  } catch (error) {
    console.error('Update financial system error:', error);
    throw error;
  }
};

export const deleteFinancialSystem = async (systemId) => {
  try {
    const { error } = await supabase?.from('financial_systems')?.delete()?.eq('id', systemId);

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      }
      console.log('Delete financial system error:', error?.message);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Delete financial system error:', error);
    throw error;
  }
};

export const completeSystem = async (userId, systemId, notes, improvementMetric) => {
  try {
    const completionData = {
      system_id: systemId,
      user_id: userId,
      notes: notes || null,
      improvement_metric: improvementMetric || 1.01
    };

    const { data, error } = await supabase?.from('system_completions')?.insert(completionData)?.select()?.single();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      }
      console.log('Complete system error:', error?.message);
      return null;
    }

    return convertKeysToCamelCase(data);
  } catch (error) {
    console.error('Complete system error:', error);
    throw error;
  }
};

// Transactions Operations
export const getTransactions = async (userId) => {
  try {
    const { data, error } = await supabase?.from('transactions')?.select('*')?.eq('user_id', userId)?.order('date', { ascending: false });

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      }
      if (error?.code === 'PGRST116') return [];
      console.log('Get transactions error:', error?.message);
      return [];
    }

    return convertKeysToCamelCase(data || []);
  } catch (error) {
    console.error('Get transactions error:', error);
    throw error;
  }
};

export const createTransaction = async (userId, transaction) => {
  try {
    const snakeCaseTransaction = convertKeysToSnakeCase({ ...transaction, userId });
    const { data, error } = await supabase?.from('transactions')?.insert(snakeCaseTransaction)?.select()?.single();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      }
      console.log('Create transaction error:', error?.message);
      return null;
    }

    return convertKeysToCamelCase(data);
  } catch (error) {
    console.error('Create transaction error:', error);
    throw error;
  }
};

// Update Transaction
export const updateTransaction = async (transactionId, updates) => {
  try {
    const snakeCaseUpdates = convertKeysToSnakeCase(updates);
    const { data, error } = await supabase
      ?.from('transactions')
      ?.update(snakeCaseUpdates)
      ?.eq('id', transactionId)
      ?.select()
      ?.single();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      }
      console.log('Update transaction error:', error?.message);
      return null;
    }

    return convertKeysToCamelCase(data);
  } catch (error) {
    console.error('Update transaction error:', error);
    throw error;
  }
};

// Delete Transaction
export const deleteTransaction = async (transactionId) => {
  try {
    const { error } = await supabase
      ?.from('transactions')
      ?.delete()
      ?.eq('id', transactionId);

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      }
      console.log('Delete transaction error:', error?.message);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Delete transaction error:', error);
    throw error;
  }
};

// Investments Operations
export const getInvestments = async (userId) => {
  try {
    const { data, error } = await supabase?.from('investments')?.select('*')?.eq('user_id', userId)?.order('created_at', { ascending: false });

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      }
      if (error?.code === 'PGRST116') return [];
      console.log('Get investments error:', error?.message);
      return [];
    }

    return convertKeysToCamelCase(data || []);
  } catch (error) {
    console.error('Get investments error:', error);
    throw error;
  }
};

// Create Investment
export const createInvestment = async (userId, investment) => {
  try {
    const snakeCaseInvestment = convertKeysToSnakeCase({ ...investment, userId });
    const { data, error } = await supabase
      ?.from('investments')
      ?.insert(snakeCaseInvestment)
      ?.select()
      ?.single();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      }
      console.log('Create investment error:', error?.message);
      return null;
    }

    return convertKeysToCamelCase(data);
  } catch (error) {
    console.error('Create investment error:', error);
    throw error;
  }
};

// Update Investment
export const updateInvestment = async (investmentId, updates) => {
  try {
    const snakeCaseUpdates = convertKeysToSnakeCase(updates);
    const { data, error } = await supabase
      ?.from('investments')
      ?.update(snakeCaseUpdates)
      ?.eq('id', investmentId)
      ?.select()
      ?.single();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      }
      console.log('Update investment error:', error?.message);
      return null;
    }

    return convertKeysToCamelCase(data);
  } catch (error) {
    console.error('Update investment error:', error);
    throw error;
  }
};

// Delete Investment
export const deleteInvestment = async (investmentId) => {
  try {
    const { error } = await supabase
      ?.from('investments')
      ?.delete()
      ?.eq('id', investmentId);

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      }
      console.log('Delete investment error:', error?.message);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Delete investment error:', error);
    throw error;
  }
};

// Saved Reports Operations
export const getSavedReports = async (userId) => {
  try {
    const { data, error } = await supabase
      ?.from('saved_reports')
      ?.select('*')
      ?.eq('user_id', userId)
      ?.order('created_at', { ascending: false });

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      }
      if (error?.code === 'PGRST116') return [];
      console.log('Get saved reports error:', error?.message);
      return [];
    }

    return convertKeysToCamelCase(data || []);
  } catch (error) {
    console.error('Get saved reports error:', error);
    throw error;
  }
};

export const createSavedReport = async (userId, report) => {
  try {
    const snakeCaseReport = convertKeysToSnakeCase({ ...report, userId });
    const { data, error } = await supabase
      ?.from('saved_reports')
      ?.insert(snakeCaseReport)
      ?.select()
      ?.single();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      }
      console.log('Create saved report error:', error?.message);
      return null;
    }

    return convertKeysToCamelCase(data);
  } catch (error) {
    console.error('Create saved report error:', error);
    throw error;
  }
};

export const updateSavedReport = async (reportId, updates) => {
  try {
    const snakeCaseUpdates = convertKeysToSnakeCase(updates);
    const { data, error } = await supabase
      ?.from('saved_reports')
      ?.update(snakeCaseUpdates)
      ?.eq('id', reportId)
      ?.select()
      ?.single();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      }
      console.log('Update saved report error:', error?.message);
      return null;
    }

    return convertKeysToCamelCase(data);
  } catch (error) {
    console.error('Update saved report error:', error);
    throw error;
  }
};

export const deleteSavedReport = async (reportId) => {
  try {
    const { error } = await supabase
      ?.from('saved_reports')
      ?.delete()
      ?.eq('id', reportId);

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      }
      console.log('Delete saved report error:', error?.message);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Delete saved report error:', error);
    throw error;
  }
};

// Retirement Plans Operations
export const getRetirementPlans = async (userId) => {
  try {
    const { data, error } = await supabase
      ?.from('retirement_plans')
      ?.select('*')
      ?.eq('user_id', userId)
      ?.order('created_at', { ascending: false });

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      }
      if (error?.code === 'PGRST116') return [];
      console.log('Get retirement plans error:', error?.message);
      return [];
    }

    return convertKeysToCamelCase(data || []);
  } catch (error) {
    console.error('Get retirement plans error:', error);
    throw error;
  }
};

export const updateRetirementPlan = async (planId, updates) => {
  try {
    const snakeCaseUpdates = convertKeysToSnakeCase(updates);
    const { data, error } = await supabase
      ?.from('retirement_plans')
      ?.update(snakeCaseUpdates)
      ?.eq('id', planId)
      ?.select()
      ?.single();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      }
      console.log('Update retirement plan error:', error?.message);
      return null;
    }

    return convertKeysToCamelCase(data);
  } catch (error) {
    console.error('Update retirement plan error:', error);
    throw error;
  }
};

export const deleteRetirementPlan = async (planId) => {
  try {
    const { error } = await supabase
      ?.from('retirement_plans')
      ?.delete()
      ?.eq('id', planId);

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      }
      console.log('Delete retirement plan error:', error?.message);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Delete retirement plan error:', error);
    throw error;
  }
};

// Retirement Scenarios Operations
export const getRetirementScenarios = async (planId) => {
  try {
    const { data, error } = await supabase
      ?.from('retirement_scenarios')
      ?.select('*')
      ?.eq('retirement_plan_id', planId)
      ?.order('scenario_type', { ascending: true });

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      }
      if (error?.code === 'PGRST116') return [];
      console.log('Get retirement scenarios error:', error?.message);
      return [];
    }

    return convertKeysToCamelCase(data || []);
  } catch (error) {
    console.error('Get retirement scenarios error:', error);
    throw error;
  }
};

export const updateRetirementScenario = async (scenarioId, updates) => {
  try {
    const snakeCaseUpdates = convertKeysToSnakeCase(updates);
    const { data, error } = await supabase
      ?.from('retirement_scenarios')
      ?.update(snakeCaseUpdates)
      ?.eq('id', scenarioId)
      ?.select()
      ?.single();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      }
      console.log('Update retirement scenario error:', error?.message);
      return null;
    }

    return convertKeysToCamelCase(data);
  } catch (error) {
    console.error('Update retirement scenario error:', error);
    throw error;
  }
};

export const deleteRetirementScenario = async (scenarioId) => {
  try {
    const { error } = await supabase
      ?.from('retirement_scenarios')
      ?.delete()
      ?.eq('id', scenarioId);

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      }
      console.log('Delete retirement scenario error:', error?.message);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Delete retirement scenario error:', error);
    throw error;
  }
};

// Connected Accounts Operations
export const getConnectedAccounts = async (userId) => {
  try {
    const { data, error } = await supabase?.from('connected_accounts')?.select('*')?.eq('user_id', userId)?.order('created_at', { ascending: false });

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      }
      if (error?.code === 'PGRST116') return [];
      console.log('Get connected accounts error:', error?.message);
      return [];
    }

    return convertKeysToCamelCase(data || []);
  } catch (error) {
    console.error('Get connected accounts error:', error);
    throw error;
  }
};

export const createConnectedAccount = async (userId, account) => {
  try {
    const snakeCaseAccount = convertKeysToSnakeCase({ ...account, userId });
    const { data, error } = await supabase?.from('connected_accounts')?.insert(snakeCaseAccount)?.select()?.single();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      }
      console.log('Create connected account error:', error?.message);
      return null;
    }

    return convertKeysToCamelCase(data);
  } catch (error) {
    console.error('Create connected account error:', error);
    throw error;
  }
};

// Update Connected Account
export const updateConnectedAccount = async (accountId, updates) => {
  try {
    const snakeCaseUpdates = convertKeysToSnakeCase(updates);
    const { data, error } = await supabase
      ?.from('connected_accounts')
      ?.update(snakeCaseUpdates)
      ?.eq('id', accountId)
      ?.select()
      ?.single();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      }
      console.log('Update connected account error:', error?.message);
      return null;
    }

    return convertKeysToCamelCase(data);
  } catch (error) {
    console.error('Update connected account error:', error);
    throw error;
  }
};

// Delete Connected Account
export const deleteConnectedAccount = async (accountId) => {
  try {
    const { error } = await supabase
      ?.from('connected_accounts')
      ?.delete()
      ?.eq('id', accountId);

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      }
      console.log('Delete connected account error:', error?.message);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Delete connected account error:', error);
    throw error;
  }
};

// Notifications Operations
export const getNotifications = async (userId, limit = 10) => {
  try {
    const { data, error } = await supabase?.from('notifications')?.select('*')?.eq('user_id', userId)?.order('created_at', { ascending: false })?.limit(limit);

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      }
      if (error?.code === 'PGRST116') return [];
      console.log('Get notifications error:', error?.message);
      return [];
    }

    return convertKeysToCamelCase(data || []);
  } catch (error) {
    console.error('Get notifications error:', error);
    throw error;
  }
};

export const markNotificationAsRead = async (notificationId) => {
  try {
    const { data, error } = await supabase?.from('notifications')?.update({ is_read: true })?.eq('id', notificationId)?.select()?.single();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      }
      console.log('Mark notification as read error:', error?.message);
      return null;
    }

    return convertKeysToCamelCase(data);
  } catch (error) {
    console.error('Mark notification as read error:', error);
    throw error;
  }
};

export const createNotification = async (userId, notification) => {
  try {
    const snakeCaseNotification = convertKeysToSnakeCase({ ...notification, userId });
    const { data, error } = await supabase?.from('notifications')?.insert(snakeCaseNotification)?.select()?.single();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      }
      console.log('Create notification error:', error?.message);
      return null;
    }

    return convertKeysToCamelCase(data);
  } catch (error) {
    console.error('Create notification error:', error);
    throw error;
  }
};

export const snoozeNotification = async (notificationId, hours = 1) => {
  try {
    const snoozeUntil = new Date();
    snoozeUntil?.setHours(snoozeUntil?.getHours() + hours);

    const { data, error } = await supabase
      ?.from('notifications')
      ?.update({ 
        metadata: { snoozed_until: snoozeUntil?.toISOString() },
        is_read: true 
      })
      ?.eq('id', notificationId)
      ?.select()
      ?.single();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      }
      console.log('Snooze notification error:', error?.message);
      return null;
    }

    return convertKeysToCamelCase(data);
  } catch (error) {
    console.error('Snooze notification error:', error);
    throw error;
  }
};

export const dismissNotification = async (notificationId) => {
  try {
    const { error } = await supabase
      ?.from('notifications')
      ?.delete()
      ?.eq('id', notificationId);

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      }
      console.log('Dismiss notification error:', error?.message);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Dismiss notification error:', error);
    throw error;
  }
};

// Send Email Notification
export const sendEmailNotification = async (emailType, to, data) => {
  try {
    const response = await fetch(
      `${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/send-email`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env?.VITE_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({
          emailType,
          to,
          data
        })
      }
    );

    if (!response?.ok) {
      const error = await response?.json();
      throw new Error(error.error || 'Failed to send email');
    }

    const result = await response?.json();
    return result;
  } catch (error) {
    console.error('Send email notification error:', error);
    throw error;
  }
};

// Create notification with optional email delivery
export const createNotificationWithEmail = async (userId, notification, sendEmail = false) => {
  try {
    // Create in-app notification
    const createdNotification = await createNotification(userId, notification);

    // Send email if enabled and user preferences allow
    if (sendEmail && createdNotification) {
      const preferences = await getNotificationPreferences(userId);
      const userProfile = await getUserProfile(userId);

      if (preferences?.emailEnabled && userProfile?.email) {
        // Map notification type to email type
        const emailTypeMap = {
          spending_threshold: 'spending_threshold',
          budget_limit: 'budget_alert',
          goal_progress: 'goal_milestone',
          bill_reminder: 'bill_reminder',
          investment_alert: 'investment_alert',
          system_reminder: 'system_reminder'
        };

        const emailType = emailTypeMap?.[notification?.notificationType];
        if (emailType) {
          try {
            await sendEmailNotification(
              emailType,
              userProfile?.email,
              notification?.metadata || {}
            );
          } catch (emailError) {
            console.error('Failed to send email notification:', emailError);
            // Don't throw - in-app notification was created successfully
          }
        }
      }
    }

    return createdNotification;
  } catch (error) {
    console.error('Create notification with email error:', error);
    throw error;
  }
};

// MFA Operations
export const getMFASettings = async (userId) => {
  try {
    const { data, error } = await supabase
      ?.from('mfa_settings')
      ?.select('*')
      ?.eq('user_id', userId)
      ?.single();

    if (error) {
      if (error?.code === 'PGRST116') {
        // No MFA settings found, return default
        return { isEnabled: false };
      }
      console.log('Get MFA settings error:', error?.message);
      return { isEnabled: false };
    }

    return convertKeysToCamelCase(data);
  } catch (error) {
    console.error('Get MFA settings error:', error);
    return { isEnabled: false };
  }
};

export const enableMFA = async (userId, email) => {
  try {
    // Generate secret key (in production, this should be done server-side)
    const secretKey = Array.from({ length: 32 }, () => 
      'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567'?.[Math.floor(Math.random() * 32)]
    )?.join('');

    const { data, error } = await supabase
      ?.from('mfa_settings')
      ?.upsert({ 
        user_id: userId, 
        secret_key: secretKey,
        is_enabled: false // Will be enabled after verification
      })
      ?.select()
      ?.single();

    if (error) {
      console.log('Enable MFA error:', error?.message);
      throw error;
    }

    return { secretKey };
  } catch (error) {
    console.error('Enable MFA error:', error);
    throw error;
  }
};

export const verifyMFACode = async (userId, code) => {
  try {
    // In production, verification should be done server-side with proper TOTP validation
    // For now, we'll simulate verification
    const { data, error } = await supabase
      ?.from('mfa_settings')
      ?.update({ 
        is_enabled: true,
        last_used_at: new Date()?.toISOString()
      })
      ?.eq('user_id', userId)
      ?.select()
      ?.single();

    if (error) {
      console.log('Verify MFA code error:', error?.message);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Verify MFA code error:', error);
    return false;
  }
};

export const disableMFA = async (userId) => {
  try {
    const { error } = await supabase
      ?.from('mfa_settings')
      ?.update({ is_enabled: false })
      ?.eq('user_id', userId);

    if (error) {
      console.log('Disable MFA error:', error?.message);
      throw error;
    }

    return true;
  } catch (error) {
    console.error('Disable MFA error:', error);
    throw error;
  }
};

export const generateBackupCodes = async (userId) => {
  try {
    // Generate 10 backup codes
    const codes = Array.from({ length: 10 }, () => 
      Array.from({ length: 8 }, () => 
        '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ'?.[Math.floor(Math.random() * 36)]
      )?.join('')
    );

    const { error } = await supabase
      ?.from('mfa_settings')
      ?.update({ backup_codes: codes })
      ?.eq('user_id', userId);

    if (error) {
      console.log('Generate backup codes error:', error?.message);
      throw error;
    }

    return codes;
  } catch (error) {
    console.error('Generate backup codes error:', error);
    throw error;
  }
};

// Audit Logging
export const logAuditEvent = async (userId, action, resourceType, resourceId, metadata = null) => {
  try {
    const { data, error } = await supabase
      ?.from('audit_logs')
      ?.insert({
        user_id: userId,
        action,
        resource_type: resourceType,
        resource_id: resourceId,
        metadata
      })
      ?.select()
      ?.single();

    if (error) {
      console.log('Log audit event error:', error?.message);
      return null;
    }

    return convertKeysToCamelCase(data);
  } catch (error) {
    console.error('Log audit event error:', error);
    return null;
  }
};

export const getAuditLogs = async (userId, limit = 50) => {
  try {
    const { data, error } = await supabase
      ?.from('audit_logs')
      ?.select('*')
      ?.eq('user_id', userId)
      ?.order('created_at', { ascending: false })
      ?.limit(limit);

    if (error) {
      if (error?.code === 'PGRST116') return [];
      console.log('Get audit logs error:', error?.message);
      return [];
    }

    return convertKeysToCamelCase(data || []);
  } catch (error) {
    console.error('Get audit logs error:', error);
    return [];
  }
};

// Data Export/Import
export const exportData = async (userId, exportType, fileFormat) => {
  try {
    const { data, error } = await supabase
      ?.from('data_exports')
      ?.insert({
        user_id: userId,
        export_type: exportType,
        file_format: fileFormat,
        status: 'pending',
        expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)?.toISOString() // 7 days
      })
      ?.select()
      ?.single();

    if (error) {
      console.log('Export data error:', error?.message);
      throw error;
    }

    // In production, this would trigger a background job to generate the export
    // For now, we'll simulate it
    await logAuditEvent(userId, 'export', 'data_export', data?.id, { export_type: exportType, file_format: fileFormat });

    return convertKeysToCamelCase(data);
  } catch (error) {
    console.error('Export data error:', error);
    throw error;
  }
};

export const importData = async (userId, data) => {
  try {
    // In production, this would validate and import data in batches
    // For now, we'll just log the audit event
    await logAuditEvent(userId, 'import', 'data_import', null, { record_count: Object.keys(data)?.length });
    
    return true;
  } catch (error) {
    console.error('Import data error:', error);
    throw error;
  }
};

export const getDataExports = async (userId) => {
  try {
    const { data, error } = await supabase
      ?.from('data_exports')
      ?.select('*')
      ?.eq('user_id', userId)
      ?.order('created_at', { ascending: false });

    if (error) {
      if (error?.code === 'PGRST116') return [];
      console.log('Get data exports error:', error?.message);
      return [];
    }

    return convertKeysToCamelCase(data || []);
  } catch (error) {
    console.error('Get data exports error:', error);
    return [];
  }
};

// API Tokens
export const createAPIToken = async (userId, tokenName, scopes = []) => {
  try {
    // Generate token (in production, use crypto.randomBytes)
    const token = Array.from({ length: 32 }, () => 
      '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ'?.[Math.floor(Math.random() * 62)]
    )?.join('');

    const tokenHash = btoa(token); // In production, use proper hashing

    const { data, error } = await supabase
      ?.from('api_tokens')
      ?.insert({
        user_id: userId,
        token_name: tokenName,
        token_hash: tokenHash,
        scopes,
        is_active: true
      })
      ?.select()
      ?.single();

    if (error) {
      console.log('Create API token error:', error?.message);
      throw error;
    }

    await logAuditEvent(userId, 'create', 'api_token', data?.id, { token_name: tokenName });

    return { ...convertKeysToCamelCase(data), token }; // Return token only once
  } catch (error) {
    console.error('Create API token error:', error);
    throw error;
  }
};

export const getAPITokens = async (userId) => {
  try {
    const { data, error } = await supabase
      ?.from('api_tokens')
      ?.select('id, token_name, token_type, scopes, last_used_at, expires_at, is_active, created_at')
      ?.eq('user_id', userId)
      ?.order('created_at', { ascending: false });

    if (error) {
      if (error?.code === 'PGRST116') return [];
      console.log('Get API tokens error:', error?.message);
      return [];
    }

    return convertKeysToCamelCase(data || []);
  } catch (error) {
    console.error('Get API tokens error:', error);
    return [];
  }
};

export const revokeAPIToken = async (tokenId) => {
  try {
    const { error } = await supabase
      ?.from('api_tokens')
      ?.update({ is_active: false })
      ?.eq('id', tokenId);

    if (error) {
      console.log('Revoke API token error:', error?.message);
      throw error;
    }

    return true;
  } catch (error) {
    console.error('Revoke API token error:', error);
    throw error;
  }
};

// Compliance Reporting
export const generateComplianceReport = async (userId, reportType, taxYear) => {
  try {
    // In production, this would generate a comprehensive report
    // For now, we'll create a record and simulate report generation
    const reportData = {
      reportType,
      taxYear,
      generatedAt: new Date()?.toISOString(),
      summary: {
        totalIncome: 0,
        totalExpenses: 0,
        netIncome: 0,
        taxableIncome: 0
      }
    };

    const { data, error } = await supabase
      ?.from('compliance_reports')
      ?.insert({
        user_id: userId,
        report_type: reportType,
        tax_year: taxYear,
        report_data: reportData
      })
      ?.select()
      ?.single();

    if (error) {
      console.log('Generate compliance report error:', error?.message);
      throw error;
    }

    await logAuditEvent(userId, 'create', 'compliance_report', data?.id, { report_type: reportType, tax_year: taxYear });

    return convertKeysToCamelCase(data);
  } catch (error) {
    console.error('Generate compliance report error:', error);
    throw error;
  }
};

export const getComplianceReports = async (userId) => {
  try {
    const { data, error } = await supabase
      ?.from('compliance_reports')
      ?.select('*')
      ?.eq('user_id', userId)
      ?.order('generated_at', { ascending: false });

    if (error) {
      if (error?.code === 'PGRST116') return [];
      console.log('Get compliance reports error:', error?.message);
      return [];
    }

    return convertKeysToCamelCase(data || []);
  } catch (error) {
    console.error('Get compliance reports error:', error);
    return [];
  }
};

// Webhooks
export const createWebhook = async (userId, webhookUrl, webhookSecret, events) => {
  try {
    const { data, error } = await supabase
      ?.from('webhooks')
      ?.insert({
        user_id: userId,
        webhook_url: webhookUrl,
        webhook_secret: webhookSecret,
        events,
        is_active: true
      })
      ?.select()
      ?.single();

    if (error) {
      console.log('Create webhook error:', error?.message);
      throw error;
    }

    await logAuditEvent(userId, 'create', 'webhook', data?.id, { webhook_url: webhookUrl, events });

    return convertKeysToCamelCase(data);
  } catch (error) {
    console.error('Create webhook error:', error);
    throw error;
  }
};

export const getWebhooks = async (userId) => {
  try {
    const { data, error } = await supabase
      ?.from('webhooks')
      ?.select('*')
      ?.eq('user_id', userId)
      ?.order('created_at', { ascending: false });

    if (error) {
      if (error?.code === 'PGRST116') return [];
      console.log('Get webhooks error:', error?.message);
      return [];
    }

    return convertKeysToCamelCase(data || []);
  } catch (error) {
    console.error('Get webhooks error:', error);
    return [];
  }
};

export const deleteWebhook = async (webhookId) => {
  try {
    const { error } = await supabase
      ?.from('webhooks')
      ?.delete()
      ?.eq('id', webhookId);

    if (error) {
      console.log('Delete webhook error:', error?.message);
      throw error;
    }

    return true;
  } catch (error) {
    console.error('Delete webhook error:', error);
    throw error;
  }
};

// Linked Bank Accounts Operations
export const getLinkedBankAccounts = async (userId) => {
  try {
    const { data, error } = await supabase?.from('linked_bank_accounts')?.select('*')?.eq('user_id', userId)?.order('created_at', { ascending: false });

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      }
      if (error?.code === 'PGRST116') return [];
      console.log('Get linked bank accounts error:', error?.message);
      return [];
    }

    return convertKeysToCamelCase(data || []);
  } catch (error) {
    console.error('Get linked bank accounts error:', error);
    throw error;
  }
};

export const createLinkedBankAccount = async (userId, account) => {
  try {
    const snakeCaseAccount = convertKeysToSnakeCase({ ...account, userId });
    const { data, error } = await supabase?.from('linked_bank_accounts')?.insert(snakeCaseAccount)?.select()?.single();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      }
      console.log('Create linked bank account error:', error?.message);
      return null;
    }

    return convertKeysToCamelCase(data);
  } catch (error) {
    console.error('Create linked bank account error:', error);
    throw error;
  }
};

export const updateLinkedBankAccount = async (accountId, updates) => {
  try {
    const snakeCaseUpdates = convertKeysToSnakeCase(updates);
    const { data, error } = await supabase
      ?.from('linked_bank_accounts')
      ?.update(snakeCaseUpdates)
      ?.eq('id', accountId)
      ?.select()
      ?.single();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      }
      console.log('Update linked bank account error:', error?.message);
      return null;
    }

    return convertKeysToCamelCase(data);
  } catch (error) {
    console.error('Update linked bank account error:', error);
    throw error;
  }
};

export const deleteLinkedBankAccount = async (accountId) => {
  try {
    const { error } = await supabase
      ?.from('linked_bank_accounts')
      ?.delete()
      ?.eq('id', accountId);

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      }
      console.log('Delete linked bank account error:', error?.message);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Delete linked bank account error:', error);
    throw error;
  }
};

// Transaction Imports Operations
export const getTransactionImports = async (userId) => {
  try {
    const { data, error } = await supabase?.from('transaction_imports')?.select('*')?.eq('user_id', userId)?.order('created_at', { ascending: false });

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      }
      if (error?.code === 'PGRST116') return [];
      console.log('Get transaction imports error:', error?.message);
      return [];
    }

    return convertKeysToCamelCase(data || []);
  } catch (error) {
    console.error('Get transaction imports error:', error);
    throw error;
  }
};

export const createTransactionImport = async (userId, importData) => {
  try {
    const snakeCaseImport = convertKeysToSnakeCase({ ...importData, userId });
    const { data, error } = await supabase?.from('transaction_imports')?.insert(snakeCaseImport)?.select()?.single();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      }
      console.log('Create transaction import error:', error?.message);
      return null;
    }

    return convertKeysToCamelCase(data);
  } catch (error) {
    console.error('Create transaction import error:', error);
    throw error;
  }
};

export const updateTransactionImport = async (importId, updates) => {
  try {
    const snakeCaseUpdates = convertKeysToSnakeCase(updates);
    const { data, error } = await supabase
      ?.from('transaction_imports')
      ?.update(snakeCaseUpdates)
      ?.eq('id', importId)
      ?.select()
      ?.single();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      }
      console.log('Update transaction import error:', error?.message);
      return null;
    }

    return convertKeysToCamelCase(data);
  } catch (error) {
    console.error('Update transaction import error:', error);
    throw error;
  }
};

// Sync Logs Operations
export const getSyncLogs = async (userId, accountId = null) => {
  try {
    let query = supabase?.from('sync_logs')?.select('*')?.eq('user_id', userId);
    
    if (accountId) {
      query = query?.eq('linked_account_id', accountId);
    }
    
    const { data, error } = await query?.order('started_at', { ascending: false })?.limit(50);

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      }
      if (error?.code === 'PGRST116') return [];
      console.log('Get sync logs error:', error?.message);
      return [];
    }

    return convertKeysToCamelCase(data || []);
  } catch (error) {
    console.error('Get sync logs error:', error);
    throw error;
  }
};

export const createSyncLog = async (userId, logData) => {
  try {
    const snakeCaseLog = convertKeysToSnakeCase({ ...logData, userId });
    const { data, error } = await supabase?.from('sync_logs')?.insert(snakeCaseLog)?.select()?.single();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      }
      console.log('Create sync log error:', error?.message);
      return null;
    }

    return convertKeysToCamelCase(data);
  } catch (error) {
    console.error('Create sync log error:', error);
    throw error;
  }
};

export const updateSyncLog = async (logId, updates) => {
  try {
    const snakeCaseUpdates = convertKeysToSnakeCase(updates);
    const { data, error } = await supabase
      ?.from('sync_logs')
      ?.update(snakeCaseUpdates)
      ?.eq('id', logId)
      ?.select()
      ?.single();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      }
      console.log('Update sync log error:', error?.message);
      return null;
    }

    return convertKeysToCamelCase(data);
  } catch (error) {
    console.error('Update sync log error:', error);
    throw error;
  }
};

// Receipt Images Operations
export const getReceiptImages = async (userId) => {
  try {
    const { data, error } = await supabase?.from('receipt_images')?.select('*')?.eq('user_id', userId)?.order('created_at', { ascending: false });

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      }
      if (error?.code === 'PGRST116') return [];
      console.log('Get receipt images error:', error?.message);
      return [];
    }

    return convertKeysToCamelCase(data || []);
  } catch (error) {
    console.error('Get receipt images error:', error);
    throw error;
  }
};

export const createReceiptImage = async (userId, receiptData) => {
  try {
    const snakeCaseReceipt = convertKeysToSnakeCase({ ...receiptData, userId });
    const { data, error } = await supabase?.from('receipt_images')?.insert(snakeCaseReceipt)?.select()?.single();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      }
      console.log('Create receipt image error:', error?.message);
      return null;
    }

    return convertKeysToCamelCase(data);
  } catch (error) {
    console.error('Create receipt image error:', error);
    throw error;
  }
};

export const updateReceiptImage = async (receiptId, updates) => {
  try {
    const snakeCaseUpdates = convertKeysToSnakeCase(updates);
    const { data, error } = await supabase
      ?.from('receipt_images')
      ?.update(snakeCaseUpdates)
      ?.eq('id', receiptId)
      ?.select()
      ?.single();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      }
      console.log('Update receipt image error:', error?.message);
      return null;
    }

    return convertKeysToCamelCase(data);
  } catch (error) {
    console.error('Update receipt image error:', error);
    throw error;
  }
};

export const deleteReceiptImage = async (receiptId) => {
  try {
    const { error } = await supabase
      ?.from('receipt_images')
      ?.delete()
      ?.eq('id', receiptId);

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      }
      console.log('Delete receipt image error:', error?.message);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Delete receipt image error:', error);
    throw error;
  }
};

// Receipt Storage Operations
export const uploadReceiptImage = async (userId, file) => {
  try {
    const fileExt = file?.name?.split('.')?.pop();
    const fileName = `${Date.now()}-${Math.random()?.toString(36)?.substring(7)}.${fileExt}`;
    const filePath = `${userId}/${fileName}`;

    const { data, error } = await supabase?.storage
      ?.from('receipts')
      ?.upload(filePath, file, {
        cacheControl: '3600',
        upsert: false
      });

    if (error) {
      console.error('Upload receipt error:', error?.message);
      throw error;
    }

    return {
      path: data?.path,
      fullPath: data?.fullPath || filePath
    };
  } catch (error) {
    console.error('Upload receipt error:', error);
    throw error;
  }
};

export const getReceiptImageUrl = async (path) => {
  try {
    const { data, error } = await supabase?.storage
      ?.from('receipts')
      ?.createSignedUrl(path, 3600); // 1 hour expiry

    if (error) {
      console.error('Get receipt URL error:', error?.message);
      throw error;
    }

    return data?.signedUrl;
  } catch (error) {
    console.error('Get receipt URL error:', error);
    throw error;
  }
};

export const deleteReceiptFromStorage = async (path) => {
  try {
    const { error } = await supabase?.storage
      ?.from('receipts')
      ?.remove([path]);

    if (error) {
      console.error('Delete receipt from storage error:', error?.message);
      throw error;
    }

    return true;
  } catch (error) {
    console.error('Delete receipt from storage error:', error);
    throw error;
  }
};
function createBill(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: createBill is not implemented yet.', args);
  return null;
}

export { createBill };
function updateBill(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: updateBill is not implemented yet.', args);
  return null;
}

export { updateBill };
function deleteBill(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: deleteBill is not implemented yet.', args);
  return null;
}

export { deleteBill };